import { clsx } from "clsx";
import { ComponentPropsWithRef, useEffect, useRef, useState } from "react";
import DotLoader, { DotLoaderProps } from "./DotLoader.tsx";
import OffScreen from "./OffScreen.tsx";
import { FormattedMessage } from "react-intl";
import BaseLinkOrButton from "./BaseLinkOrButton.tsx";
import styles from "./LoaderButton.module.scss";

type LoaderButtonProps = ComponentPropsWithRef<"button"> & {
  loading: boolean;
  loadingContext?: string;
  loaderProps?: DotLoaderProps;
  href?: string;
  to?: string;
  keepSameWidth?: boolean;
};

export function LoaderButton({
  loading,
  loadingContext,
  children,
  className,
  loaderProps,
  onClick,
  type,
  href,
  keepSameWidth = false,
  ...restProps
}: LoaderButtonProps) {
  const loaderDefaults = {
    dotCount: 3,
    color: "white",
    size: "small",
  } as DotLoaderProps;
  const ref = useRef<HTMLButtonElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const elDots = ref.current.querySelector("[data-dots]") as
        | HTMLElement
        | undefined;
      if (keepSameWidth && elDots) {
        const dir = window.getComputedStyle(elDots).direction;
        setOffset((elDots.offsetWidth / 2) * (dir === "ltr" ? 1 : -1));
      } else {
        setOffset(0);
      }
    }
  }, [keepSameWidth]);

  return (
    <BaseLinkOrButton
      ref={ref}
      className={clsx(className, styles.button)}
      onClick={loading ? undefined : onClick}
      href={href}
      type={type === "submit" && loading ? "button" : type}
      {...restProps}
    >
      <span
        style={{
          transform: `translateX(${loading ? 0 : offset}px)`,
        }}
      >
        {children}
      </span>
      {(keepSameWidth || loading) && (
        <DotLoader
          data-dots
          className={styles.dots}
          style={{
            visibility: loading
              ? "visible"
              : keepSameWidth
              ? "hidden"
              : undefined,
          }}
          {...loaderDefaults}
          {...loaderProps}
        />
      )}
      <OffScreen tagName="span" role="status" aria-live="polite">
        {loading && (
          <>
            {loadingContext && <span>{loadingContext}</span>}
            <FormattedMessage defaultMessage="Loading, please wait" />
          </>
        )}
      </OffScreen>
    </BaseLinkOrButton>
  );
}

export default LoaderButton;
